import type { OrderFinancialVO } from "../OrderForms/orders";

export interface ZuuStep2Draft {
  meta: {
    isFinancialInfoRequired: boolean;
    activeSection: "financial-info" | "fna";
  };
  financialInfo: {
    availableIncome: {
      monthlyIncomeLast24Months: {
        questionCode: "1a";
        amount: number;
        source:
          | "derived_display_only"
          | "field_bound_input"
          | "temporary_frontend_input";
      };
      monthlyExpenseLast24Months: {
        questionCode: "1b";
        amount: number;
        source:
          | "derived_display_only"
          | "field_bound_input"
          | "temporary_frontend_input";
      };
      monthlyDisposableIncomeAfterRetirement: {
        questionCode: "1c";
        amount: number;
        source: "backend_pending" | "temporary_frontend_input";
      };
    };
    liquidAssets: {
      currentLiquidAssets: {
        questionCode: "2a";
        amount: number;
        source:
          | "derived_display_only"
          | "field_bound_input"
          | "temporary_frontend_input";
      };
      additionalLiquidAssetsAfterRetirement: {
        questionCode: "2b";
        amount: number;
        source: "backend_pending" | "temporary_frontend_input";
      };
    };
  };
}

function sum(values: Array<number | null | undefined>) {
  return values.reduce((acc, value) => acc + (value ?? 0), 0);
}

function toPositiveInteger(value: unknown): number | undefined {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return undefined;
  }
  const integer = Math.trunc(value);
  return integer >= 0 ? integer : undefined;
}

export function deriveZuuStep2Draft(input: {
  financial?: OrderFinancialVO;
  activeSection: "financial-info" | "fna";
  isFinancialInfoRequired?: boolean;
}): ZuuStep2Draft {
  const financial = input.financial ?? {};
  const existingDraft = (financial as any).zuuStep2Draft ?? {};
  const existingFinancialInfo = existingDraft.financialInfo ?? {};
  const existingAvailableIncome = existingFinancialInfo.availableIncome ?? {};
  const existingLiquidAssets = existingFinancialInfo.liquidAssets ?? {};
  const monthlyIncomesTotal = toPositiveInteger(
    (financial as any).monthlyIncomesTotal,
  );
  const monthlyExpensesTotal = toPositiveInteger(
    (financial as any).monthlyExpensesTotal,
  );
  const liquidAssetsTotal = toPositiveInteger(
    (financial as any).liquidAssetsTotal,
  );
  const apiAvgMonthlyIncome = toPositiveInteger(
    financial.income?.avgMonthlyIncome,
  );
  const apiAvgMonthlyExpense = toPositiveInteger(
    financial.expense?.avgMonthlyExpense,
  );
  const apiExpectedRetireIncome =
    toPositiveInteger(financial.income?.expectedRetireIncome) ?? null;
  const apiCurrentLiquidAssets = toPositiveInteger(
    financial.currentAssets?.currentLiquidAssets,
  );
  const apiExpectedRetireLiquidAssets =
    toPositiveInteger(financial.currentAssets?.expectedRetireLiquidAssets) ??
    null;

  const totalIncome = sum([
    financial.income?.salaryCommissionBonus,
    financial.income?.rentalIncome,
    financial.income?.hasOtherIncome
      ? financial.income?.otherIncomeAmount
      : 0,
  ]);

  const totalExpense = sum([
    financial.expense?.personalAndFamilyExpense,
    financial.expense?.mortgageLoanRepayment,
    financial.expense?.insuranceContribution,
    financial.expense?.hasOtherExpense
      ? financial.expense?.otherExpenseAmount
      : 0,
  ]);

  const totalCurrentAssets = sum([
    financial.currentAssets?.cashAndDeposits,
    financial.currentAssets?.liquidInvestments,
    financial.currentAssets?.hasOtherCurrentAssets
      ? financial.currentAssets?.otherCurrentAssetsAmount
      : 0,
  ]);

  return {
    meta: {
      isFinancialInfoRequired: input.isFinancialInfoRequired === true,
      activeSection: input.activeSection,
    },
    financialInfo: {
      availableIncome: {
        monthlyIncomeLast24Months: {
          questionCode: "1a",
          amount:
            monthlyIncomesTotal ??
            apiAvgMonthlyIncome ??
            toPositiveInteger(
              existingAvailableIncome.monthlyIncomeLast24Months?.amount,
            ) ??
            totalIncome,
          source:
            monthlyIncomesTotal != null
              ? "field_bound_input"
              : apiAvgMonthlyIncome != null
                ? "field_bound_input"
              : existingAvailableIncome.monthlyIncomeLast24Months?.amount != null
                ? "temporary_frontend_input"
                : "derived_display_only",
        },
        monthlyExpenseLast24Months: {
          questionCode: "1b",
          amount:
            monthlyExpensesTotal ??
            apiAvgMonthlyExpense ??
            toPositiveInteger(
              existingAvailableIncome.monthlyExpenseLast24Months?.amount,
            ) ??
            totalExpense,
          source:
            monthlyExpensesTotal != null
              ? "field_bound_input"
              : apiAvgMonthlyExpense != null
                ? "field_bound_input"
              : existingAvailableIncome.monthlyExpenseLast24Months?.amount != null
                ? "temporary_frontend_input"
                : "derived_display_only",
        },
        monthlyDisposableIncomeAfterRetirement: {
          questionCode: "1c",
          amount:
            apiExpectedRetireIncome ??
            existingAvailableIncome.monthlyDisposableIncomeAfterRetirement
              ?.amount ??
            0,
          source:
            apiExpectedRetireIncome != null
              ? "temporary_frontend_input"
              : existingAvailableIncome.monthlyDisposableIncomeAfterRetirement
                  ?.amount != null
              ? "temporary_frontend_input"
              : "backend_pending",
        },
      },
      liquidAssets: {
        currentLiquidAssets: {
          questionCode: "2a",
          amount:
            liquidAssetsTotal ??
            apiCurrentLiquidAssets ??
            toPositiveInteger(existingLiquidAssets.currentLiquidAssets?.amount) ??
            totalCurrentAssets,
          source:
            liquidAssetsTotal != null
              ? "field_bound_input"
              : apiCurrentLiquidAssets != null
                ? "field_bound_input"
              : existingLiquidAssets.currentLiquidAssets?.amount != null
                ? "temporary_frontend_input"
                : "derived_display_only",
        },
        additionalLiquidAssetsAfterRetirement: {
          questionCode: "2b",
          amount:
            apiExpectedRetireLiquidAssets ??
            existingLiquidAssets.additionalLiquidAssetsAfterRetirement?.amount ??
            0,
          source:
            apiExpectedRetireLiquidAssets != null
              ? "temporary_frontend_input"
              : existingLiquidAssets.additionalLiquidAssetsAfterRetirement?.amount !=
                null
              ? "temporary_frontend_input"
              : "backend_pending",
        },
      },
    },
  };
}
